import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  BUSINESS,
  CITY_LOCAL_DATA,
  findCity,
  findTradeAngle,
  TRADE_LOCAL_ANGLES,
  CITIES,
} from "@/lib/site-data";
import { AnswerBlock } from "@/components/AnswerBlock";
import { CallNowButton } from "@/components/CallNowButton";
import { FaqAccordion } from "@/components/FaqAccordion";
import { ServiceArea } from "@/components/ServiceArea";
import { InternalLinkNetwork } from "@/components/InternalLinkNetwork";

export const Route = createFileRoute("/seo/$trade/$city")({
  loader: ({ params }) => {
    const trade = findTradeAngle(params.trade);
    const city = findCity(params.city);
    if (!trade || !city) throw notFound();
    const local = CITY_LOCAL_DATA[city.slug];
    return { trade, city, local };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Not found" }] };
    const { trade, city } = loaderData;
    const cap = (s: string) => s.replace(/^\w/, (c) => c.toUpperCase());
    const title = `${cap(trade.trade)} SEO in ${city.name}, ${city.region} | ${BUSINESS.shortName}`;
    const description = `SEO and Google Ads built for ${trade.trade}s in ${city.name}, ${city.region}. Map Pack, Local Service Ads, and ${trade.searchTerm} rankings — done locally.`;
    const canonical = `https://lehitradessaeo.com/seo/${params.trade}/${params.city}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: canonical },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: `${cap(trade.trade)} SEO`,
            areaServed: {
              "@type": "City",
              name: `${city.name}, ${city.region}`,
            },
            provider: { "@id": "https://lehitradessaeo.com/#organization" },
            url: canonical,
            description,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://lehitradessaeo.com/" },
              { "@type": "ListItem", position: 2, name: "Local trade pages", item: "https://lehitradessaeo.com/seo" },
              { "@type": "ListItem", position: 3, name: `${cap(trade.trade)} SEO in ${city.name}`, item: canonical },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-6 py-20 text-center">
      <h1 className="font-display text-3xl">Page not found</h1>
      <p className="mt-2 text-muted-foreground">
        <Link to="/seo" className="underline">Browse all local trade pages</Link>
      </p>
    </main>
  ),
  component: SeoCityTradePage,
});

function cap(s: string) {
  return s.replace(/^\w/, (c) => c.toUpperCase());
}

function SeoCityTradePage() {
  const { trade, city, local } = Route.useLoaderData();
  const tradeLabel = cap(trade.trade);

  const related = TRADE_LOCAL_ANGLES.filter((t) => t.slug !== trade.slug);
  const otherCities = CITIES.filter((c) => c.slug !== city.slug).slice(0, 6);
  const canonical = `https://lehitradessaeo.com/seo/${trade.slug}/${city.slug}`;

  const faqs = [
    {
      q: `How long until a ${city.name} ${trade.trade} sees ranking results?`,
      a: `Most ${city.name} trades clients see Map Pack movement in 60–90 days and meaningful lead volume by month 4–6. We share a 90-day baseline so the trajectory is visible before any renewal decision.`,
    },
    {
      q: `What does a ${trade.trade} marketing program in ${city.name} cost?`,
      a: `Typical programs run $1,500–$4,500/mo depending on how many ${city.name} neighborhoods you want to dominate and whether Local Service Ads are included. We publish all benchmarks on our ROI calculator.`,
    },
    {
      q: `Do you handle Google Business Profile for ${city.name} ${trade.trade}s?`,
      a: `Yes — categories, services, photos from real ${city.name} jobs, weekly posts, and a review-velocity workflow tuned to ${city.name}'s ${local?.growthRate ?? "active"} housing market.`,
    },
    {
      q: `Will you build separate pages for every city around ${city.name}?`,
      a: `That's the whole strategy. Each city you serve gets its own indexable page with real local content — no spun copy or duplicate templates.`,
    },
  ];

  return (
    <main className="mx-auto max-w-3xl px-6 py-16">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
        Local trade page · {city.name}, {city.region}
      </p>
      <h1 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">
        {tradeLabel} SEO in {city.name}, {city.region}
      </h1>
      <p className="mt-5 text-xl text-muted-foreground">
        We help {trade.trade}s in {city.name} rank for {trade.searchTerm}, win
        the Map Pack, and run Local Service Ads that pay back inside 90 days.
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        <Link
          to="/contact"
          className="inline-flex items-center rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Get a free {city.name} audit
        </Link>
        <CallNowButton variant="primary" label={`Call ${BUSINESS.phone}`} />
      </div>

      <AnswerBlock
        id="answer-block"
        question={`How does a ${trade.trade} in ${city.name} get more leads from Google?`}
        answer={
          <>
            A {trade.trade} in {city.name} wins Google by combining a {city.name}-specific
            landing page, an actively managed Google Business Profile, and Local
            Service Ads — tuned for the {trade.searchTerm} searches local
            homeowners actually run.
          </>
        }
        bullets={[
          `${city.name}-specific service page with real neighborhood proof`,
          `Google Business Profile reviews and weekly posts targeting "${trade.searchTerm}"`,
          `Local Service Ads with the Google Guaranteed badge for ${city.name}`,
        ]}
      />

      <section className="mt-12">
        <h2 className="font-display text-2xl tracking-tight">Why {city.name} is different</h2>
        {local ? (
          <ul className="mt-4 space-y-3 text-base text-foreground/90">
            <li>
              <strong>Population:</strong> ~{local.population.toLocaleString()} residents.
              {" "}A {local.growthRate}-growth market — campaign cadence and city-page depth need to match.
            </li>
            <li>
              <strong>Median home age:</strong> ~{local.medianHomeAgeYears} years. {local.housingNote}
            </li>
            <li>
              <strong>Weather hook:</strong> {local.weatherHook}
            </li>
            <li>
              <strong>Known neighborhoods:</strong> {city.neighborhoods.join(", ")}.
            </li>
          </ul>
        ) : (
          <p className="mt-3 text-muted-foreground">{city.blurb}</p>
        )}
      </section>

      <ServiceArea
        title={`${tradeLabel} SEO — other cities we serve`}
        serviceType={`${tradeLabel} SEO`}
        description={`SEO and Google Ads for ${trade.trade}s across Utah County.`}
        url={canonical}
        areas={CITIES.map((c) => ({ name: c.name, region: c.region }))}
      />

      <FaqAccordion
        title={`${city.name} ${trade.trade} marketing FAQ`}
        items={faqs}
        pageUrl={canonical}
      />

      <InternalLinkNetwork
        seoTrade={trade.slug}
        cityLabel={city.name}
        focusText={`${trade.trade} ${trade.service} ${trade.searchTerm}`}
      />

      <section className="mt-12">
        <h2 className="font-display text-2xl tracking-tight">
          What we build for {city.name} {trade.trade}s
        </h2>
        <ul className="mt-4 space-y-3 text-base text-foreground/90">
          <li>
            A dedicated <strong>{city.name} {trade.service} services</strong> page
            written for local intent — emergency response, common call types ({trade.emergencyExample}),
            and high-ticket installs ({trade.highTicketExample}).
          </li>
          <li>
            GBP optimization with categories, services, photos from real {city.name}
            jobs, and a weekly post cadence.
          </li>
          <li>
            Local Service Ads setup with Google Guaranteed verification and daily
            lead disputes so {city.name} cost-per-lead stays honest.
          </li>
          <li>
            Review-velocity workflow tuned for {city.name}'s {local?.growthRate ?? "active"}
            {" "}housing market.
          </li>
          <li>
            Schema markup (LocalBusiness, Service, FAQPage) so AI Overviews and
            ChatGPT cite you for "best {trade.trade} in {city.name}".
          </li>
        </ul>
      </section>

      <section className="mt-12 rounded-2xl border border-border bg-card p-8">
        <h3 className="font-display text-xl tracking-tight">
          Free {city.name} {trade.service} marketing audit
        </h3>
        <p className="mt-2 text-muted-foreground">
          We'll show you exactly where your rankings, GBP, and ads stand against
          the top {trade.trade}s in {city.name}. No pitch deck, no contract pressure.
        </p>
        <Link
          to="/contact"
          className="mt-5 inline-flex rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Request my audit
        </Link>
      </section>

      <section className="mt-12 grid gap-6 md:grid-cols-2">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Other trades we serve in {city.name}
          </p>
          <ul className="mt-3 space-y-1 text-sm">
            {related.map((t) => (
              <li key={t.slug}>
                <Link
                  to="/seo/$trade/$city"
                  params={{ trade: t.slug, city: city.slug }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {cap(t.trade)} SEO in {city.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            {tradeLabel} SEO in other cities
          </p>
          <ul className="mt-3 space-y-1 text-sm">
            {otherCities.map((c) => (
              <li key={c.slug}>
                <Link
                  to="/seo/$trade/$city"
                  params={{ trade: trade.slug, city: c.slug }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {tradeLabel} SEO in {c.name}, {c.region}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </main>
  );
}